import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Users, Plus, Trash2, ChevronDown, ChevronUp, 
  CheckCircle, AlertCircle, ArrowLeft, UserPlus, Lock, Mail, Eye, EyeOff, PlayCircle, FileText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { FamilyFormSection } from '../../components/FamilySections';
import { EmailFieldHint } from '../../components/EmailFieldHint';
import { isValidEmailFormat } from '../../lib/emailValidation';
import DISTRICTS_BY_STATE from '../../data/indiaDistricts';


const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const FamilyRegistrationForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [topTab, setTopTab] = useState('notes');
  const [options, setOptions] = useState({
    states: [],
    nukhs: [],
    mainBranches: [],
    education: [],
    occupation: [],
    maritalStatus: [],
    relations: [],
    countries: [],
    statesByCountry: {},
    citiesByCountry: {},
    citiesByState: {}
  });
  
  // Family level data - separate current and origin location
  const [familyData, setFamilyData] = useState({
    // Current Location
    currentCountry: 'India',
    currentState: '',
    currentDistrict: '',
    currentCity: '',
    currentPlace: '',
    // Origin Location
    originCountry: 'India',
    originState: '',
    originDistrict: '',
    originCity: '',
    originPlace: '',
    // Family Info
    familyNukh: ''
  });
  
  // Login credentials for family head (mandatory)
  const [loginCredentials, setLoginCredentials] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  const [sameAsCurrent, setSameAsCurrent] = useState(false);
  const handleSameAsCurrent = (checked) => {
    setSameAsCurrent(checked);
    if (checked) {
      setFamilyData(prev => ({
        ...prev,
        originCountry: prev.currentCountry,
        originState: prev.currentState,
        originDistrict: prev.currentDistrict,
        originCity: prev.currentCity,
        originPlace: prev.currentPlace,
      }));
    }
  };
  
  // Family members array
  const [familyMembers, setFamilyMembers] = useState([
    createEmptyMember(true) // First member is Head
  ]);
  
  const [expandedMembers, setExpandedMembers] = useState([0]); // First member expanded by default
  const [submitted, setSubmitted] = useState(false);
  const [loginCreated, setLoginCreated] = useState(false);

  // Create empty member object
  function createEmptyMember(isHead = false) {
    return {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      name: '',
      gender: 'Male',
      fatherName: '',
      nukh: '',
      spouseName: '',
      relationWithHead: isHead ? 'Head - Self' : '',
      dob: '',
      age: '',
      education: '',
      occupation: '',
      maritalStatus: '',
      bloodGroup: '',
      mobileNo: '',
      emailId: '',
      socialMediaId: '',
      maternalName: '',
      maternalNukh: '',
      maternalPlace: '',
      other: ''
    };
  }

  // Fetch dropdown options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        // Fetch family registration options
        const response = await fetch(`${API_URL}/api/family-registration/options`);
        if (response.ok) {
          const data = await response.json();
          setOptions(prev => ({ ...prev, ...data }));
        }
        
        // Fetch professional options for countries/states/cities
        const profResponse = await fetch(`${API_URL}/api/professionals/options`);
        if (profResponse.ok) {
          const profData = await profResponse.json();
          setOptions(prev => ({
            ...prev,
            countries: profData.countries || [],
            statesByCountry: profData.statesByCountry || {},
            citiesByCountry: profData.citiesByCountry || {},
            citiesByState: profData.citiesByState || {}
          }));
        }
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    };
    fetchOptions();
  }, []);

  // Check if country is "Other" (requires manual text input)
  const isOtherCountry = (country) => country === 'Other';

  // Get states based on selected country
  const getStatesForCountry = (country) => {
    if (!country || !options.statesByCountry || isOtherCountry(country)) return [];
    return options.statesByCountry[country] || options.states || [];
  };

  // Get cities based on state (for India) or country (for others)
  const getCitiesForState = (country, state) => {
    if (!country || isOtherCountry(country)) return [];
    
    // For India, use state-specific cities
    if (country === 'India' && state && options.citiesByState) {
      return options.citiesByState[state] || [];
    }
    
    // For other countries, use country-level cities
    if (options.citiesByCountry) {
      return options.citiesByCountry[country] || [];
    }
    
    return [];
  };

  // Refresh local city options immediately after a custom city is saved,
  // so it appears in both Current & Native Place dropdowns without a page refresh.
  const handleCustomCitySaved = (cityName, ctry, st) => {
    setOptions(prev => {
      if (ctry === 'India' && st) {
        const list = prev.citiesByState?.[st] || [];
        if (list.includes(cityName)) return prev;
        return {
          ...prev,
          citiesByState: {
            ...prev.citiesByState,
            [st]: [...list, cityName].sort((a, b) => a.localeCompare(b)),
          },
        };
      }
      if (ctry) {
        const list = prev.citiesByCountry?.[ctry] || [];
        if (list.includes(cityName)) return prev;
        return {
          ...prev,
          citiesByCountry: {
            ...prev.citiesByCountry,
            [ctry]: [...list, cityName].sort((a, b) => a.localeCompare(b)),
          },
        };
      }
      return prev;
    });
  };

  // Get districts based on state (only for India)
  const getDistrictsForState = (country, state) => {
    if (!state || country !== 'India') return [];
    return DISTRICTS_BY_STATE[state] || [];
  };

  // Reset state, district and city when current country changes
  useEffect(() => {
    if (familyData.currentCountry) {
      setFamilyData(prev => ({
        ...prev,
        currentState: '',
        currentDistrict: '',
        currentCity: ''
      }));
    }
  }, [familyData.currentCountry]);

  // Reset district and city when current state changes
  useEffect(() => {
    if (familyData.currentState) {
      setFamilyData(prev => ({
        ...prev,
        currentDistrict: '',
        currentCity: ''
      }));
    }
  }, [familyData.currentState]);

  // Reset state, district and city when origin country changes
  useEffect(() => {
    if (familyData.originCountry) {
      setFamilyData(prev => ({
        ...prev,
        originState: '',
        originDistrict: '',
        originCity: ''
      }));
    }
  }, [familyData.originCountry]);

  // Reset district and city when origin state changes
  useEffect(() => {
    if (familyData.originState) {
      setFamilyData(prev => ({
        ...prev,
        originDistrict: '',
        originCity: ''
      }));
    }
  }, [familyData.originState]);

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return '';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age > 0 ? age : '';
  };

  // Handle family data change
  const handleFamilyDataChange = (e) => {
    const { name, value } = e.target;
    setFamilyData(prev => ({ ...prev, [name]: value }));
  };

  // Handle member data change
  const handleMemberChange = (index, field, value) => {
    setFamilyMembers(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      
      // Auto-calculate age when DOB changes
      if (field === 'dob') {
        updated[index].age = calculateAge(value);
      }
      
      return updated;
    });
  };

  // Add new member
  const addMember = () => {
    const newMember = createEmptyMember(false);
    setFamilyMembers(prev => [...prev, newMember]);
    setExpandedMembers(prev => [...prev, familyMembers.length]);
  };

  // Remove member
  const removeMember = (index) => {
    if (familyMembers.length === 1) {
      toast.error('At least one family member is required');
      return;
    }
    if (index === 0) {
      toast.error('Cannot remove family head');
      return;
    }
    setFamilyMembers(prev => prev.filter((_, i) => i !== index));
    setExpandedMembers(prev => prev.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  // Toggle member expansion
  const toggleMember = (index) => {
    setExpandedMembers(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  // Validate form
  const validateForm = () => {
    // Validate current location
    if (!familyData.currentCountry) {
      toast.error('Please select current country');
      return false;
    }
    if (!familyData.currentState) {
      toast.error('Please select current state');
      return false;
    }
    if (!familyData.currentCity) {
      toast.error('Please select or enter current city / village');
      return false;
    }
    if (!familyData.currentPlace?.trim()) {
      toast.error('Please enter current address');
      return false;
    }
    if (!familyData.familyNukh) {
      toast.error('Please select family nukh');
      return false;
    }
    
    for (let i = 0; i < familyMembers.length; i++) {
      const member = familyMembers[i];
      const memberLabel = member.name || `Member ${i + 1}`;
      
      if (!member.name.trim()) {
        toast.error(`Please enter name for member ${i + 1}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.gender) {
        toast.error(`Please select gender for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.relationWithHead) {
        toast.error(`Please select relation with head for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.fatherName?.trim()) {
        toast.error(`Please enter father's name for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.nukh) {
        toast.error(`Please select nukh for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.dob) {
        toast.error(`Please enter date of birth for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.education) {
        toast.error(`Please select education for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.occupation) {
        toast.error(`Please select occupation for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.maritalStatus) {
        toast.error(`Please select marital status for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (i === 0 && !member.mobileNo?.trim()) {
        toast.error(`Please enter mobile number for ${memberLabel} (Family Head)`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.maternalName?.trim()) {
        toast.error(`Please enter maternal name (नाना/मामा का नाम) for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.maternalNukh) {
        toast.error(`Please select maternal nukh for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (!member.maternalPlace?.trim()) {
        toast.error(`Please enter maternal place for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
      if (member.emailId?.trim() && !isValidEmailFormat(member.emailId)) {
        toast.error(`Please enter a valid email address for ${memberLabel}`);
        setExpandedMembers(prev => prev.includes(i) ? prev : [...prev, i]);
        return false;
      }
    }
    
    // Validate login credentials (mandatory)
    if (!loginCredentials.email.trim()) {
      toast.error('Please enter email for login');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(loginCredentials.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!loginCredentials.password || loginCredentials.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (loginCredentials.password !== loginCredentials.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    
    return true;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Map frontend fields to backend expected fields
      const payload = {
        currentPlace: familyData.currentPlace || '',
        currentCity: familyData.currentCity || '',
        currentDistrict: familyData.currentDistrict || '',
        district: familyData.currentDistrict || familyData.currentCity || '',
        state: familyData.currentState || '',
        country: familyData.currentCountry || 'India',
        originPlace: familyData.originPlace || '',
        originCity: familyData.originCity || '',
        originDistrict: familyData.originDistrict || '',
        originState: familyData.originState || '',
        originCountry: familyData.originCountry || 'India',
        familyNukh: familyData.familyNukh || '',
        familyMembers: familyMembers.map(member => ({
          ...member,
          age: member.age ? parseInt(member.age) : null
        })),
        // Login credentials are mandatory now
        headEmail: loginCredentials.email,
        headPassword: loginCredentials.password
      };
      
      const response = await fetch(`${API_URL}/api/family-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubmitted(true);
        setLoginCreated(data.loginCreated || false);
        if (data.loginCreated) {
          toast.success('Family registered with login credentials! You can now login to edit your family data.');
        } else {
          toast.success('Family registration submitted successfully!');
        }
      } else {
        const error = await response.json();
        console.error('Server error:', error);
        toast.error(error.detail || 'Failed to submit registration');
      }
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Registration Successful!
          </h2>
          <p className="text-gray-600 mb-4">
            Your family has been successfully registered. Thank you for contributing to our community directory.
          </p>
          
          {loginCreated && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-green-800 mb-2 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                Login Credentials Created
              </h3>
              <p className="text-sm text-green-700 mb-2">
                You can now login to edit your family data anytime using:
              </p>
              <p className="text-sm text-green-800">
                <strong>Email:</strong> {loginCredentials.email}
              </p>
              <p className="text-xs text-green-600 mt-2">
                Use "Family Login" option on the main login page.
              </p>
            </div>
          )}
          
          <div className="space-y-3">
            {loginCreated && (
              <Button
                onClick={() => navigate('/auth?tab=family')}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                <Lock className="w-4 h-4 mr-2" />
                Login Now
              </Button>
            )}
            <Button
              onClick={() => navigate('/families-register')}
              className={`w-full ${loginCreated ? '' : 'bg-primary hover:bg-primary-600'}`}
              variant={loginCreated ? 'outline' : 'default'}
            >
              Back to Families Register
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSubmitted(false);
                setLoginCreated(false);
                setFamilyData({
                  currentCountry: 'India',
                  currentState: '',
                  currentDistrict: '',
                  currentCity: '',
                  currentPlace: '',
                  originCountry: 'India',
                  originState: '',
                  originDistrict: '',
                  originCity: '',
                  originPlace: '',
                  familyNukh: ''
                });
                setLoginCredentials({
                  email: '',
                  password: '',
                  confirmPassword: ''
                });
                setFamilyMembers([createEmptyMember(true)]);
                setExpandedMembers([0]);
              }}
              className="w-full"
            >
              Register Another Family
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="gradient-orange py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white/80 text-sm mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>›</span>
            <Link to="/families-register" className="hover:text-white">Families Register</Link>
            <span>›</span>
            <span>Registration Form</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/families-register" className="text-white/80 hover:text-white">
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-white">Family Registration Form</h1>
              <p className="text-white/80 mt-1">परिवार पंजीकरण फॉर्म</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Video + Notes Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6 shadow-sm"
          data-testid="family-form-help-tabs"
        >
          {/* Tab bar */}
          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setTopTab('video')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm sm:text-base font-semibold min-h-[48px] transition-colors ${
                topTab === 'video'
                  ? 'bg-orange-50 text-orange-800 border-b-2 border-orange-500'
                  : 'text-gray-500 hover:text-orange-700 hover:bg-orange-50/50'
              }`}
              data-testid="help-tab-video"
            >
              <PlayCircle className="w-5 h-5 flex-shrink-0" />
              <span>वीडियो / How to Fill</span>
            </button>
            <button
              type="button"
              onClick={() => setTopTab('notes')}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm sm:text-base font-semibold min-h-[48px] transition-colors ${
                topTab === 'notes'
                  ? 'bg-amber-50 text-amber-800 border-b-2 border-amber-500'
                  : 'text-gray-500 hover:text-amber-700 hover:bg-amber-50/50'
              }`}
              data-testid="help-tab-notes"
            >
              <FileText className="w-5 h-5 flex-shrink-0" />
              <span>निर्देश / Notes</span>
            </button>
          </div>

          {/* Video panel */}
          {topTab === 'video' && (
            <div className="p-4" data-testid="family-form-video-tutorial">
              <video
                controls
                playsInline
                preload="metadata"
                className="w-full rounded-lg"
                style={{ maxHeight: '420px' }}
                data-testid="family-form-tutorial-video"
              >
                <source src="https://customer-assets.emergentagent.com/job_abcfafc4-09e8-4929-917e-c56a46d51339/artifacts/pyn5xjpq_Family%20Form%20Tutorial.mp4" type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              <p className="text-xs text-gray-500 mt-2 text-center">फॉर्म भरने से पहले यह वीडियो जरूर देखें</p>
            </div>
          )}

          {/* Notes panel */}
          {topTab === 'notes' && (
            <div className="bg-amber-50 p-5" data-testid="family-form-notes">
              <h3 className="font-bold text-amber-800 mb-1 text-base">📌 कृपया ध्यान दें</h3>
              <p className="text-amber-700 text-sm mb-3">फैमिली फॉर्म भरने से पूर्व निम्न बातों का विशेष ध्यान रखें:</p>
              <ol className="space-y-2.5 text-amber-700 text-sm">
                <li>
                  <p className="font-semibold text-amber-800">🔹 1. जन्म तिथि एवं ननिहाल की जानकारी</p>
                  <p>फैमिली फॉर्म भरने से पहले परिवार के सभी सदस्यों की जन्म तिथि की जानकारी तैयार रखें। इसी प्रकार ननिहाल पक्ष का विवरण भी पहले से ज्ञात कर लें।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 2. फैमिली हैड — पुरुष या महिला</p>
                  <p>फैमिली हैड पुरुष अथवा महिला, कोई भी हो सकता है। यदि परिवार की मुखिया महिला हैं तो Gender में Female Select करें।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 3. लॉगइन के लिए ईमेल एवं पासवर्ड</p>
                  <p>फैमिली हैड के लॉगइन के लिए Valid Email ID देना आवश्यक है। अपना पासवर्ड स्वयं बनायें और उसे सुरक्षित रखकर याद रखें। भविष्य में परिवार की जानकारी को Edit/Update करने के लिए इसकी आवश्यकता होगी।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 4. मूल निवास का विवरण</p>
                  <p>यदि आपका मूल निवास वर्तमान निवास से अलग है तो मूल निवास का विवरण भी अवश्य भरें। शहर/गांव के लिए Dropdown List से स्थान का चयन करें। यदि आपका स्थान सूची में उपलब्ध नहीं है तो उसे Add भी कर सकते हैं।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 5. Dropdown का प्रयोग करें</p>
                  <p>नुख, वैवाहिक स्थिति, मुखिया से संबंध, शिक्षा, Occupation आदि फील्ड में उपलब्ध Dropdown List का ही प्रयोग करें।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 6. परिवार के सभी सदस्यों की जानकारी भरें</p>
                  <p>मुखिया का विवरण भरने के बाद “Add Another Family Member” बटन दबायें और परिवार के सभी सदस्यों का विवरण क्रमवार भरें।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 7. विवाहित बेटियों का विवरण</p>
                  <p>विवाहित बेटियों के नाम पीहर परिवार में Add न करें। उनके नाम एवं विवरण उनके ससुराल परिवार में शामिल किये जायेंगे। चाहें तो विवाहित बेटियों का नाम एवं विवरण Other Information फील्ड में दिया जा सकता है।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 8. परिवार के सदस्यों का क्रम</p>
                  <p>परिवार के सदस्यों का विवरण निम्न क्रम में रखें:</p>
                  <p>मुखिया → जीवन साथी → बड़ा पुत्र/पुत्री → बड़े पुत्र की पत्नी → उनके बच्चे → छोटा पुत्र/पुत्री → छोटे पुत्र की पत्नी → उनके बच्चे → आगे इसी क्रम में।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 9. माता की नुख</p>
                  <p>माता की Nukh फील्ड में माता के पीहर पक्ष की नुख Select करें।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 10. मोबाइल नम्बर</p>
                  <p>फैमिली हैड का मोबाइल नम्बर देना अनिवार्य है। अन्य सदस्यों के मोबाइल नम्बर देना ऐच्छिक है। इच्छा हो तो दें, अन्यथा छोड़ सकते हैं। यदि मोबाइल नम्बर व ईमेल आईडी देंगे तो सूचनायें भेजने में उपयोगी सिद्ध होंगे।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 11. Submit करने से पहले जांच करें</p>
                  <p>फॉर्म पूरा भरने के बाद सभी विवरणों को एक बार ध्यानपूर्वक Check अवश्य करें और उसके बाद Submit बटन दबायें। स्क्रीन पर “Submitted Successfully” का मैसेज दिखाई देने तक प्रतीक्षा करें।</p>
                </li>
                <li>
                  <p className="font-semibold text-amber-800">🔹 12. बाद में संशोधन कैसे करें?</p>
                  <p>फॉर्म Submit करने के बाद यदि किसी जानकारी में संशोधन करना हो तो अपने Login से प्रवेश करें और “Edit My Family” पर जाकर आवश्यक संशोधन करें।</p>
                </li>
              </ol>
              <p className="text-amber-800 text-sm font-medium mt-3">🙏 आपका सही एवं पूर्ण विवरण ही समाज की सटीक डिजिटल फैमिली डायरेक्टरी का आधार है।</p>
            </div>
          )}
        </motion.div>

        <form onSubmit={handleSubmit}>
          {/* Current Location Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-md p-6 mb-6"
          >
            <h2 className="text-xl font-bold text-emerald-800 mb-6 flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-emerald-100 text-emerald-700 shrink-0">
                <Users className="w-5 h-5" />
              </span>
              Current Place
              <span className="text-sm font-normal text-gray-500">(वर्तमान निवास)</span>
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                  <span className="text-gray-400 text-xs ml-1">(देश)</span>
                </label>
                <select
                  name="currentCountry"
                  value={familyData.currentCountry}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                >
                  <option value="">Select Country</option>
                  {options.countries?.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State / Province <span className="text-red-500">*</span>
                  <span className="text-gray-400 text-xs ml-1">(राज्य)</span>
                </label>
                {isOtherCountry(familyData.currentCountry) ? (
                  <input
                    type="text"
                    name="currentState"
                    value={familyData.currentState}
                    onChange={handleFamilyDataChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter state/province name"
                    required
                  />
                ) : (
                  <select
                    name="currentState"
                    value={familyData.currentState}
                    onChange={handleFamilyDataChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    required
                    disabled={!familyData.currentCountry}
                  >
                    <option value="">Select State</option>
                    {getStatesForCountry(familyData.currentCountry).map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District <span className="text-red-500">*</span>
                  <span className="text-gray-400 text-xs ml-1">(जिला)</span>
                </label>
                {isOtherCountry(familyData.currentCountry) || familyData.currentCountry !== 'India' ? (
                  <input
                    type="text"
                    name="currentDistrict"
                    value={familyData.currentDistrict}
                    onChange={handleFamilyDataChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter district name"
                  />
                ) : (
                  <SearchableSelect
                    options={getDistrictsForState(familyData.currentCountry, familyData.currentState)}
                    value={familyData.currentDistrict}
                    onChange={(value) => setFamilyData(prev => ({ ...prev, currentDistrict: value }))}
                    placeholder="Search or select district..."
                    allowCustom={true}
                    disabled={!familyData.currentState}
                    name="currentDistrict"
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City / Village <span className="text-red-500">*</span>
                  <span className="text-gray-400 text-xs ml-1">(शहर / गाँव)</span>
                </label>
                {isOtherCountry(familyData.currentCountry) ? (
                  <input
                    type="text"
                    name="currentCity"
                    value={familyData.currentCity}
                    onChange={handleFamilyDataChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter city name"
                    required
                  />
                ) : (
                  <SearchableSelect
                    options={getCitiesForState(familyData.currentCountry, familyData.currentState)}
                    value={familyData.currentCity}
                    onChange={(value) => setFamilyData(prev => ({ ...prev, currentCity: value }))}
                    placeholder="Search or select city..."
                    allowCustom={true}
                    disabled={!familyData.currentState}
                    name="currentCity"
                    required
                    saveCustomCity={true}
                    country={familyData.currentCountry}
                    state={familyData.currentState}
                    onCustomCitySaved={handleCustomCitySaved}
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address <span className="text-red-500">*</span>
                  <span className="text-gray-400 text-xs ml-1">(पता)</span>
                </label>
                <input
                  type="text"
                  name="currentPlace"
                  value={familyData.currentPlace}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter full address"
                  required
                />
              </div>
            </div>
          </motion.div>

          {/* Origin Location Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 mb-6"
          >
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <h2 className="text-xl font-bold text-orange-800 flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-orange-100 text-orange-700 shrink-0">
                  <Users className="w-5 h-5" />
                </span>
                Native Place
                <span className="text-sm font-normal text-gray-500">(मूल निवास)</span>
              </h2>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none" data-testid="reg-same-as-current">
                <input
                  type="checkbox"
                  checked={sameAsCurrent}
                  onChange={(e) => handleSameAsCurrent(e.target.checked)}
                  className="w-4 h-4 accent-primary rounded"
                />
                Same as Current Place <span className="text-gray-400 text-xs">(वर्तमान निवास के समान)</span>
              </label>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                  <span className="text-gray-400 text-xs ml-1">(देश)</span>
                </label>
                <select
                  name="originCountry"
                  value={familyData.originCountry}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select Country</option>
                  {options.countries?.map(country => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  State / Province
                  <span className="text-gray-400 text-xs ml-1">(राज्य)</span>
                </label>
                {isOtherCountry(familyData.originCountry) ? (
                  <input
                    type="text"
                    name="originState"
                    value={familyData.originState}
                    onChange={handleFamilyDataChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter state/province name"
                  />
                ) : (
                  <select
                    name="originState"
                    value={familyData.originState}
                    onChange={handleFamilyDataChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    disabled={!familyData.originCountry}
                  >
                    <option value="">Select State</option>
                    {getStatesForCountry(familyData.originCountry).map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  District
                  <span className="text-gray-400 text-xs ml-1">(जिला)</span>
                </label>
                {isOtherCountry(familyData.originCountry) || familyData.originCountry !== 'India' ? (
                  <input
                    type="text"
                    name="originDistrict"
                    value={familyData.originDistrict}
                    onChange={handleFamilyDataChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter district name"
                  />
                ) : (
                  <SearchableSelect
                    options={getDistrictsForState(familyData.originCountry, familyData.originState)}
                    value={familyData.originDistrict}
                    onChange={(value) => setFamilyData(prev => ({ ...prev, originDistrict: value }))}
                    placeholder="Search or select district..."
                    allowCustom={true}
                    disabled={!familyData.originState}
                    name="originDistrict"
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City / Village
                  <span className="text-gray-400 text-xs ml-1">(शहर / गाँव)</span>
                </label>
                {isOtherCountry(familyData.originCountry) ? (
                  <input
                    type="text"
                    name="originCity"
                    value={familyData.originCity}
                    onChange={handleFamilyDataChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enter city name"
                  />
                ) : (
                  <SearchableSelect
                    options={getCitiesForState(familyData.originCountry, familyData.originState)}
                    value={familyData.originCity}
                    onChange={(value) => setFamilyData(prev => ({ ...prev, originCity: value }))}
                    placeholder="Search or select city..."
                    allowCustom={true}
                    disabled={!familyData.originState}
                    name="originCity"
                    saveCustomCity={true}
                    country={familyData.originCountry}
                    state={familyData.originState}
                    onCustomCitySaved={handleCustomCitySaved}
                  />
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                  <span className="text-gray-400 text-xs ml-1">(पता)</span>
                </label>
                <input
                  type="text"
                  name="originPlace"
                  value={familyData.originPlace}
                  onChange={handleFamilyDataChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter full address"
                />
              </div>
            </div>
          </motion.div>

          {/* Family Nukh Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-md p-6 mb-6"
          >
            <h2 className="text-xl font-bold text-purple-800 mb-6 flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-purple-100 text-purple-700 shrink-0">
                <Users className="w-5 h-5" />
              </span>
              Family Information
              <span className="text-sm font-normal text-gray-500">(परिवार जानकारी)</span>
            </h2>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Family Nukh <span className="text-red-500">*</span>
                  <span className="text-gray-400 text-xs ml-1">(परिवार नख)</span>
                </label>
                <SearchableSelect
                  options={options.nukhs || []}
                  value={familyData.familyNukh}
                  onChange={(value) => setFamilyData(prev => ({ ...prev, familyNukh: value }))}
                  placeholder="Type to search nukh..."
                  filterMode="startsWith"
                  allowCustom={false}
                  name="familyNukh"
                  required
                />
              </div>
            </div>
          </motion.div>

          {/* Family Members Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-md p-6 mb-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
                  <UserPlus className="w-5 h-5" />
                </span>
                Family Members
                <span className="text-sm font-normal text-gray-500">(परिवार के सदस्य)</span>
              </h2>
              <Button
                type="button"
                onClick={addMember}
                className="bg-primary hover:bg-primary-600"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Member
              </Button>
            </div>

            <div className="space-y-4">
              {familyMembers.map((member, index) => (
                <div
                  key={member.id}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  {/* Member Header */}
                  <div
                    className={`flex items-center justify-between p-4 cursor-pointer ${
                      index === 0 ? 'bg-primary/10' : 'bg-gray-50'
                    }`}
                    onClick={() => toggleMember(index)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-primary text-white' : 'bg-gray-300 text-gray-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {member.name || `Member ${index + 1}`}
                          {index === 0 && <span className="text-primary ml-2">(Family Head)</span>}
                        </p>
                        <p className="text-sm text-gray-500">
                          {member.relationWithHead || 'Select Relation'} • {member.gender}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {index !== 0 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeMember(index);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                      {expandedMembers.includes(index) ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Member Details */}
                  {expandedMembers.includes(index) && (
                    <div className="p-4 border-t border-gray-200">
                    <FamilyFormSection sectionKey="basic" columns={3}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={member.name}
                          onChange={(e) => handleMemberChange(index, 'name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Full Name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Relation with Head <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={member.relationWithHead}
                          onChange={(e) => handleMemberChange(index, 'relationWithHead', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          disabled={index === 0}
                          required
                        >
                          {index === 0 ? (
                            <option value="Head - Self">Head - Self</option>
                          ) : (
                            <>
                              <option value="">Select Relation</option>
                              {options.relations.filter(r => r !== 'Head - Self').map(rel => (
                                <option key={rel} value={rel}>{rel}</option>
                              ))}
                            </>
                          )}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Gender <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={member.gender}
                          onChange={(e) => handleMemberChange(index, 'gender', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          required
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select>
                      </div>
                    </FamilyFormSection>
                    <FamilyFormSection sectionKey="personal" columns={3}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Marital Status <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={member.maritalStatus}
                          onChange={(e) => handleMemberChange(index, 'maritalStatus', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          required
                        >
                          <option value="">Select Status</option>
                          {options.maritalStatus.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date of Birth <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={member.dob}
                          onChange={(e) => handleMemberChange(index, 'dob', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Age (Years)
                        </label>
                        <input
                          type="number"
                          value={member.age}
                          onChange={(e) => handleMemberChange(index, 'age', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm bg-gray-50"
                          placeholder="Auto-calculated"
                          readOnly={!!member.dob}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Education <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={member.education}
                          onChange={(e) => handleMemberChange(index, 'education', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          required
                        >
                          <option value="">Select Education</option>
                          {options.education.map(edu => (
                            <option key={edu} value={edu}>{edu}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Occupation <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={member.occupation}
                          onChange={(e) => handleMemberChange(index, 'occupation', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          required
                        >
                          <option value="">Select Occupation</option>
                          {options.occupation.map(occ => (
                            <option key={occ} value={occ}>{occ}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center h-6 mb-1 text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden">
                          <span>Blood Group</span>
                          <span className="text-gray-400 text-xs ml-1">(रक्त समूह — optional)</span>
                        </label>
                        <select
                          value={member.bloodGroup || ''}
                          onChange={(e) => handleMemberChange(index, 'bloodGroup', e.target.value)}
                          className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          data-testid={`member-${index}-blood-group`}
                        >
                          <option value="">Select Blood Group</option>
                          {['A+','A-','B+','B-','AB+','AB-','O+','O-'].map(bg => (
                            <option key={bg} value={bg}>{bg}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="flex items-center h-6 mb-1 text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden">
                          <span>Spouse Name</span>
                          <span className="text-gray-400 text-xs ml-1">(जीवनसाथी — optional)</span>
                        </label>
                        <input
                          type="text"
                          value={member.spouseName || ''}
                          onChange={(e) => handleMemberChange(index, 'spouseName', e.target.value)}
                          placeholder="Enter spouse name (if applicable)"
                          className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          data-testid={`member-${index}-spouse-name`}
                        />
                      </div>
                    </FamilyFormSection>
                    <FamilyFormSection sectionKey="paternal" columns={2}>
                      <div>
                        <label className="flex items-center h-6 mb-1 text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden">
                          <span>Father's Name <span className="text-red-500">*</span></span>
                          <span className="text-gray-400 text-xs ml-1">(पिता का नाम)</span>
                        </label>
                        <input
                          type="text"
                          value={member.fatherName}
                          onChange={(e) => handleMemberChange(index, 'fatherName', e.target.value)}
                          className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Father's Name"
                          required
                        />
                      </div>
                      <div>
                        <label className="flex items-center h-6 mb-1 text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden">
                          <span>Father's Nukh <span className="text-red-500">*</span></span>
                          <span className="text-gray-400 text-xs ml-1">(पिता का नुख)</span>
                        </label>
                        <SearchableSelect
                          options={options.nukhs || []}
                          value={member.nukh}
                          onChange={(value) => handleMemberChange(index, 'nukh', value)}
                          placeholder="Type to search nukh..."
                          filterMode="startsWith"
                          allowCustom={false}
                        />
                      </div>
                      <div>
                        <label className="flex items-center h-6 mb-1 text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden">
                          <span>Mother's Name</span>
                          <span className="text-gray-400 text-xs ml-1">(माता का नाम — optional)</span>
                        </label>
                        <input
                          type="text"
                          value={member.motherName || ''}
                          onChange={(e) => handleMemberChange(index, 'motherName', e.target.value)}
                          className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Mother's Name"
                          data-testid={`member-${index}-mother-name`}
                        />
                      </div>
                      <div>
                        <label className="flex items-center h-6 mb-1 text-sm font-medium text-gray-700 whitespace-nowrap overflow-hidden">
                          <span>Mother's Nukh</span>
                          <span className="text-gray-400 text-xs ml-1">(माता का नुख — optional)</span>
                        </label>
                        <SearchableSelect
                          options={options.nukhs || []}
                          value={member.motherNukh || ''}
                          onChange={(value) => handleMemberChange(index, 'motherNukh', value)}
                          placeholder="Type to search nukh..."
                          filterMode="startsWith"
                          allowCustom={false}
                        />
                      </div>
                    </FamilyFormSection>
                    <FamilyFormSection sectionKey="maternal" columns={3}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Maternal's Name <span className="text-red-500">*</span>
                          <span className="text-gray-400 text-xs ml-1">(नाना/मामा का नाम)</span>
                        </label>
                        <input
                          type="text"
                          value={member.maternalName}
                          onChange={(e) => handleMemberChange(index, 'maternalName', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Maternal's Name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Maternal's Nukh <span className="text-red-500">*</span>
                        </label>
                        <SearchableSelect
                          options={options.nukhs || []}
                          value={member.maternalNukh}
                          onChange={(value) => handleMemberChange(index, 'maternalNukh', value)}
                          placeholder="Type to search nukh..."
                          filterMode="startsWith"
                          allowCustom={false}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Maternal's Place <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={member.maternalPlace}
                          onChange={(e) => handleMemberChange(index, 'maternalPlace', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Maternal's Place"
                          required
                        />
                      </div>
                    </FamilyFormSection>
                    <FamilyFormSection sectionKey="contact" columns={3}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email ID
                        </label>
                        <input
                          type="email"
                          value={member.emailId}
                          onChange={(e) => handleMemberChange(index, 'emailId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Email Address"
                        />
                        <EmailFieldHint
                          value={member.emailId}
                          onAccept={(v) => handleMemberChange(index, 'emailId', v)}
                          testIdPrefix={`member-${index}-email`}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Mobile No. {index === 0 && <span className="text-red-500">*</span>}
                        </label>
                        <input
                          type="tel"
                          value={member.mobileNo}
                          onChange={(e) => handleMemberChange(index, 'mobileNo', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Mobile Number"
                          required={index === 0}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Social Media ID
                        </label>
                        <input
                          type="text"
                          value={member.socialMediaId}
                          onChange={(e) => handleMemberChange(index, 'socialMediaId', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="e.g. facebook.com/yourname"
                        />
                        <p className="text-xs text-gray-400 mt-1">Facebook, Instagram, or LinkedIn profile link</p>
                      </div>
                    </FamilyFormSection>
                    <FamilyFormSection sectionKey="other" columns={1}>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Other Information
                        </label>
                        <textarea
                          value={member.other}
                          onChange={(e) => handleMemberChange(index, 'other', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
                          placeholder="Any other information..."
                          rows={2}
                        />
                      </div>
                    </FamilyFormSection>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Add More Members Button */}
            <div className="mt-4 text-center">
              <Button
                type="button"
                variant="outline"
                onClick={addMember}
                className="border-dashed border-2"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Another Family Member
              </Button>
            </div>
          </motion.div>

          {/* Family Head Login Credentials */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white rounded-xl shadow-md p-6 mb-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
                  <Lock className="w-5 h-5" />
                </span>
                Family Head Login
                <span className="text-sm font-normal text-red-500">(Required - आवश्यक)</span>
              </h2>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-700 mb-4">
                Create login credentials to edit your family data later. You can login using the Family Login option on the main login page.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="email"
                      value={loginCredentials.email}
                      onChange={(e) => setLoginCredentials(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="your@email.com"
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <EmailFieldHint
                    value={loginCredentials.email}
                    onAccept={(v) => setLoginCredentials(prev => ({ ...prev, email: v }))}
                    testIdPrefix="login-email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                    <span className="text-gray-400 text-xs ml-1">(min 6 characters)</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="password"
                      value={loginCredentials.password}
                      onChange={(e) => setLoginCredentials(prev => ({ ...prev, password: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Create password"
                      required
                      minLength={6}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="password"
                      value={loginCredentials.confirmPassword}
                      onChange={(e) => setLoginCredentials(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Confirm password"
                      required
                      minLength={6}
                      data-testid="login-confirm-password-input"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6"
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-800">Registration Summary</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-blue-700">{familyMembers.length}</p>
                <p className="text-sm text-blue-600">Total Members</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {familyMembers.filter(m => m.gender === 'Male').length}
                </p>
                <p className="text-sm text-blue-600">Male</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-700">
                  {familyMembers.filter(m => m.gender === 'Female').length}
                </p>
                <p className="text-sm text-blue-600">Female</p>
              </div>
            </div>
          </motion.div>

          {/* Submit Button */}
          <div className="flex gap-4">
            <Link to="/families-register" className="flex-1">
              <Button type="button" variant="outline" className="w-full">
                Cancel
              </Button>
            </Link>
            <Button
              type="submit"
              className="flex-1 bg-primary hover:bg-primary-600"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Submit Registration
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FamilyRegistrationForm;
